import React, { useCallback, useMemo } from 'react';

import Divider from 'antd/lib/divider';
import Form from 'antd/lib/form';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import classnames from 'classnames';

import CheckIcon from 'features/signup/components/CheckIcon';
import { FREE_PRICE, SELECT_PLAN_ITEMS } from 'features/signup/signup-constants';
import type { PlanSelectorProps } from 'features/signup/signup-types';
import { getPricing } from 'features/signup/signup-utils';

import styles from './PlanSelector.module.scss';

const containerClass = `flex flex-column full-width ${styles.container}`;

const PlanSelector = ({ isAnnual, onChange, onPeriodChange, plan }: PlanSelectorProps): JSX.Element => {
  const isFree = plan.price === FREE_PRICE;
  const monthlyPricing = useMemo(() => getPricing(plan, false), [plan]);
  const annualPricing = useMemo(() => getPricing(plan, true), [plan]);
  const pricing = isAnnual ? annualPricing : monthlyPricing;

  const selectMonthly = useCallback(() => onPeriodChange(false), [onPeriodChange]);
  const selectAnnual = useCallback(() => onPeriodChange(true), [onPeriodChange]);

  const note = useMemo(() => {
    if (isFree) {
      return null;
    }
    if (isAnnual) {
      const saved = annualPricing.savings === null ? '' : `, you save ${annualPricing.savings} a year`;
      return `${annualPricing.total} billed once for 12 months${saved}`;
    }

    return annualPricing.savings === null ? null : `Switch to annual billing and save ${annualPricing.savings} a year`;
  }, [annualPricing, isAnnual, isFree]);

  return (
    <div className={containerClass}>
      <Form.Item className={styles.select} label="Choose your plan" labelAlign="left" name="plan" required>
        <Select className="full-width plan-select" onChange={onChange} options={SELECT_PLAN_ITEMS} />
      </Form.Item>
      {isFree ? null : (
        <Form.Item className={styles.period} label="Billing period" labelAlign="left">
          {/* each option carries its own per month price, so the difference is visible before switching */}
          <div className={styles.periodControl} role="radiogroup">
            <button
              aria-checked={!isAnnual}
              className={classnames(styles.periodOption, { [styles.periodOptionActive]: !isAnnual })}
              onClick={selectMonthly}
              role="radio"
              type="button"
            >
              <span className={styles.optionName}>Monthly</span>
              <span className={styles.optionPrice}>{monthlyPricing.amount}</span>
            </button>
            <button
              aria-checked={isAnnual}
              className={classnames(styles.periodOption, { [styles.periodOptionActive]: isAnnual })}
              onClick={selectAnnual}
              role="radio"
              type="button"
            >
              <span className={styles.optionName}>
                Annual
                {annualPricing.discount === null ? null : <span className={styles.discount}>−{annualPricing.discount}</span>}
              </span>
              <span className={styles.optionPrice}>{annualPricing.amount}</span>
            </button>
          </div>
        </Form.Item>
      )}
      <Divider className={styles.divider} />
      <div className={`full-width bg-gray-light rounded ${styles.features}`}>
        <Space direction="vertical" size={3}>
          {plan.features.map((feature) => (
            <Space key={feature}>
              <CheckIcon />
              {feature}
            </Space>
          ))}
        </Space>
        <Divider style={{ margin: '15px 0' }} />
        <div className={styles.price}>
          <span className={styles.priceAmount}>{pricing.amount}</span>
          {isFree ? null : <span className={styles.pricePeriod}>per month</span>}
        </div>
        {note === null ? null : <div className={styles.annualNote}>{note}</div>}
      </div>
    </div>
  );
};

export default PlanSelector;
